import {
  CardValue,
  ICard,
  valueToRank,
  valueToWord,
} from '../../../Models/CardModels';
import {
  IDynamicRuleSetOptions,
  ISpecialCardOptions,
} from '../../../Models/GameSetupModels';
import { IUnrevealedCard } from '../../../Models/StateModels';

type PileCard = ICard | IUnrevealedCard;

export interface IPileRule {
  direction: 'up' | 'down' | 'any';
  text: string;
}

function specialCardOptions(
  rules: IDynamicRuleSetOptions,
  value: CardValue
): ISpecialCardOptions | undefined {
  return rules.specialCards[CardValue[value] as keyof typeof CardValue];
}

function isRevealed(card: PileCard): card is ICard {
  return 'value' in card;
}

function lastNonGlassCard(
  playedCards: PileCard[],
  rules: IDynamicRuleSetOptions
) {
  return [...playedCards]
    .reverse()
    .filter(isRevealed)
    .find((card) => !specialCardOptions(rules, card.value)?.glass);
}

export function getPileRule(
  playedCards: PileCard[],
  rules: IDynamicRuleSetOptions
): IPileRule {
  const topCard = lastNonGlassCard(playedCards, rules);
  const special = topCard && specialCardOptions(rules, topCard.value);
  if (!topCard || special?.resetsThePile) {
    return { direction: 'any', text: 'Play anything' };
  }
  const rank = valueToRank(topCard.value);
  if (special?.switchesNextPlayToDescending) {
    return { direction: 'down', text: `Play ${rank} or lower` };
  }
  return { direction: 'up', text: `Play ${rank} or higher` };
}

function matchingRanks(
  rules: IDynamicRuleSetOptions,
  hasEffect: (options: ISpecialCardOptions) => boolean
) {
  return Object.entries(rules.specialCards)
    .filter(([, options]) => options && hasEffect(options))
    .map(([name]) => CardValue[name as keyof typeof CardValue])
    .sort((a, b) => a - b);
}

export function ranksWhere(
  rules: IDynamicRuleSetOptions,
  hasEffect: (options: ISpecialCardOptions) => boolean
) {
  return matchingRanks(rules, hasEffect)
    .map((value) => `${valueToRank(value)}s`)
    .join(' and ');
}

export function describeRanks(
  rules: IDynamicRuleSetOptions,
  hasEffect: (options: ISpecialCardOptions) => boolean,
  effect: string
) {
  const words = matchingRanks(rules, hasEffect).map((value) =>
    valueToWord(value, true)
  );
  return words.length ? `${words.join(' and ')} ${effect}` : '';
}

export function joinSummary(parts: string[]) {
  const summary = parts.filter(Boolean).join(', ');
  return summary.charAt(0).toUpperCase() + summary.slice(1);
}

export function getSpecialCardSummary(rules: IDynamicRuleSetOptions) {
  return joinSummary([
    describeRanks(rules, (options) => options.resetsThePile, 'reset'),
    describeRanks(rules, (options) => options.burns, 'burn'),
  ]);
}

export function getBurnChance(
  playedCards: PileCard[],
  rules: IDynamicRuleSetOptions
) {
  const topCard = playedCards[playedCards.length - 1];
  if (!topCard || !isRevealed(topCard)) {
    return undefined;
  }
  const firstOfRun = [...playedCards]
    .reverse()
    .findIndex((card) => !isRevealed(card) || card.value !== topCard.value);
  const inARow = firstOfRun === -1 ? playedCards.length : firstOfRun;
  if (rules.numberInARowToBurn - inARow !== 1) {
    return undefined;
  }
  return `One more ${valueToWord(topCard.value)} burns`;
}
